import {
  FileText,
  MapPin,
  Camera,
  Lock,
  MessageSquare,
  FileSpreadsheet,
} from 'lucide-react';
import { cn } from '../../lib/cn';

export function Features() {
  return (
    <section id="features" className="border-y border-border bg-bg-soft py-24 md:py-32">
      <div className="container-page">
        <div className="max-w-3xl">
          <p className="text-xs font-medium uppercase tracking-wider text-primary">What's in the platform</p>
          <h2 className="mt-3 text-3xl font-semibold tracking-tight text-ink md:text-4xl">
            Six things every prescription delivery should leave behind.
          </h2>
          <p className="mt-4 text-base text-ink-2">
            One platform handles intake, verification, evidence capture, and audit export. No bolt-ons,
            no spreadsheets, no driver group-text screenshots.
          </p>
        </div>

        <div className="mt-16 grid grid-cols-1 gap-4 md:grid-cols-3 md:grid-rows-2">
          <FeatureCard
            className="md:col-span-2 md:row-span-1"
            icon={FileText}
            eyebrow="Audit-defensible PDFs"
            title="Generate a chain-of-custody PDF in 5 seconds."
            body="Hand it to compliance, legal, or a patient family. Every PDF includes pickup GPS, delivery GPS, photos, signatures, and the full audit trail."
            visual={<PdfMock />}
          />

          <FeatureCard
            icon={MapPin}
            eyebrow="Geofence verification"
            title="Every pickup and delivery is GPS-verified."
            body="No more 'I delivered it.' Inside the pharmacy geofence at pickup. Inside the patient geofence at drop-off."
            visual={<MapMock />}
          />

          <FeatureCard
            icon={Camera}
            eyebrow="Photo + signature capture"
            title="Every photo permanent. Every signature timestamped."
            body="Drivers capture proof at every delivery. Stored in a HIPAA-compliant Storage bucket with RLS-enforced access."
            visual={<PhotoMock />}
          />

          <FeatureCard
            icon={Lock}
            eyebrow="Tenant isolation"
            title="HIPAA-aligned access control by default."
            body="Multi-pharmacy operators see all locations. Pharmacy staff see only their own. Enforced at the database row level."
          />

          <FeatureCard
            icon={MessageSquare}
            eyebrow="Patient SMS tracking"
            title="Real-time SMS when delivery is on the way."
            body="Opt-in. HIPAA-compliant via Twilio Enterprise. Sent only to patients who said yes."
          />

          <FeatureCard
            icon={FileSpreadsheet}
            eyebrow="CSV import from any PMS"
            title="Upload your manifest from PioneerRx, QS/1, Liberty, BestRx, Computer-Rx."
            body="Any system that exports CSV. Direct PMS API integrations on the roadmap, prioritized by customer demand."
          />
        </div>
      </div>
    </section>
  );
}

function FeatureCard({
  icon: Icon,
  eyebrow,
  title,
  body,
  visual,
  className,
}: {
  icon: typeof FileText;
  eyebrow: string;
  title: string;
  body: string;
  visual?: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'group relative flex flex-col overflow-hidden rounded-2xl border border-border bg-white p-6 shadow-sm transition-shadow hover:shadow-md',
        className,
      )}
    >
      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-soft text-primary">
        <Icon className="h-4 w-4" strokeWidth={1.75} />
      </div>
      <p className="mt-5 text-xs font-medium uppercase tracking-wider text-ink-3">{eyebrow}</p>
      <h3 className="mt-1 text-lg font-semibold leading-snug text-ink">{title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-ink-2">{body}</p>
      {visual ? <div className="mt-6 flex-1">{visual}</div> : null}
    </div>
  );
}

function PdfMock() {
  return (
    <div className="relative h-44 overflow-hidden rounded-xl border border-border bg-bg-soft p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="h-1.5 w-1.5 rounded-full bg-primary" />
          <span className="text-[10px] font-semibold uppercase tracking-wider text-primary">
            Audit · Order #
          </span>
        </div>
        <span className="text-[10px] text-ink-3">Page 1 / 4</span>
      </div>
      <div className="mt-3 space-y-1.5">
        {['Patient identity', 'Pickup at pharmacy geofence', 'Route trace', 'Delivery photo', 'Signature'].map(
          (line) => (
            <div key={line} className="flex items-center justify-between text-[11px]">
              <span className="text-ink-2">{line}</span>
              <span className="font-medium text-primary">Verified</span>
            </div>
          ),
        )}
      </div>
      <div className="mt-3 grid grid-cols-3 gap-1.5">
        <div className="h-10 rounded-md bg-border" />
        <div className="h-10 rounded-md bg-border" />
        <div className="h-10 rounded-md bg-border" />
      </div>
    </div>
  );
}

function MapMock() {
  return (
    <div className="relative h-32 overflow-hidden rounded-xl border border-border bg-bg-soft">
      <div
        className="absolute inset-0 opacity-70"
        style={{
          backgroundImage:
            'radial-gradient(circle at 22% 65%, rgba(12,97,105,0.18), transparent 38%), radial-gradient(circle at 78% 30%, rgba(12,97,105,0.18), transparent 38%)',
        }}
        aria-hidden
      />
      <svg className="absolute inset-0 h-full w-full" viewBox="0 0 200 100" preserveAspectRatio="none">
        <path d="M28 70 Q 90 75 156 35" stroke="#0C6169" strokeWidth="1.5" fill="none" strokeDasharray="3 3" />
      </svg>
      <Pin className="absolute left-[14%] top-[60%]" />
      <Pin className="absolute right-[18%] top-[26%]" />
    </div>
  );
}

function Pin({ className }: { className?: string }) {
  return (
    <span className={cn('flex h-3 w-3 items-center justify-center', className)}>
      <span className="absolute h-3 w-3 animate-beam-pulse rounded-full bg-primary/30" />
      <span className="h-1.5 w-1.5 rounded-full bg-primary" />
    </span>
  );
}

function PhotoMock() {
  return (
    <div className="grid grid-cols-3 gap-2">
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="aspect-square rounded-lg border border-border"
          style={{
            backgroundImage:
              'linear-gradient(135deg, #E8E8E6 0%, #F5F5F4 50%, #E8E8E6 100%)',
          }}
        />
      ))}
    </div>
  );
}
